import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "../components/AuthProvider.js";
import { ErrorBoundary } from "../components/ErrorBoundary.js";
import { api } from "../lib/api.js";
import { Plus, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";

function GuestDetailPageWithErrorBoundary() {
  return (
    <ErrorBoundary componentName="GuestDetailPage">
      <GuestDetailPage />
    </ErrorBoundary>
  );
}

export const Route = createFileRoute("/guests/$guestId")({ component: GuestDetailPageWithErrorBoundary });

interface Guest {
  id: string;
  name: string;
  email: string;
  phone?: string;
  title?: string;
  company?: string;
  bio?: string;
  socialLinks?: { website?: string; twitter?: string; linkedin?: string; youtube?: string };
  tags: string[];
  totalEpisodes: number;
  episodes: Array<{ id: string; title: string; status: string; scheduledAt?: string }>;
}

const statusColors: Record<string, string> = {
  inquiry: "bg-chart-3/10 text-chart-3", scheduled: "bg-primary/10 text-primary",
  recorded: "bg-chart-1/10 text-chart-1", edited: "bg-chart-5/10 text-chart-5",
  published: "bg-chart-2/10 text-chart-2", promoted: "bg-chart-4/10 text-chart-4", completed: "bg-muted text-muted-foreground",
};

function GuestDetailPage() {
  const { user } = useAuth();
  const { guestId } = Route.useParams();
  const navigate = useNavigate();
  const [guest, setGuest] = useState<Guest | null>(null);
  const [loading, setLoading] = useState(true);
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get(`/api/guests/${guestId}`).then(r => r.json()).then(d => { setGuest(d.guest); setLoading(false); });
  }, [guestId, user]);

  const handleScheduleEpisode = () => {
    setScheduling(true);
    // Navigate to new episode page with guest pre-selected
    navigate({ to: '/episodes/new', search: { guestId } });
  };

  if (!user) return <div>Loading...</div>;
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  if (!guest) return <div>Guest not found</div>;

  return (
    <div className="space-y-8">
      <div>
        <Link to="/guests" className="text-sm text-muted-foreground hover:text-foreground">← Back to guests</Link>
        <h1 className="text-3xl font-bold mt-2">{guest.name}</h1>
        <p className="text-muted-foreground">{guest.email}</p>
      </div>
      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="grid md:grid-cols-2 gap-4">
                  <div><span className="text-sm text-muted-foreground">Title</span><p>{guest.title || "—"}</p></div>
                  <div><span className="text-sm text-muted-foreground">Company</span><p>{guest.company || "—"}</p></div>
                </div>
                <div><span className="text-sm text-muted-foreground">Phone</span><p>{guest.phone || "—"}</p></div>
                {guest.bio && <div><span className="text-sm text-muted-foreground">Bio</span><p className="mt-1">{guest.bio}</p></div>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Social Links</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {guest.socialLinks?.website && <a href={guest.socialLinks.website} target="_blank" rel="noopener" className="px-3 py-1 bg-secondary rounded-lg text-sm hover:bg-secondary/80">Website</a>}
                {guest.socialLinks?.twitter && <a href={`https://twitter.com/${guest.socialLinks.twitter.replace("@", "")}`} target="_blank" rel="noopener" className="px-3 py-1 bg-secondary rounded-lg text-sm hover:bg-secondary/80">Twitter</a>}
                {guest.socialLinks?.linkedin && <a href={guest.socialLinks.linkedin.startsWith("http") ? guest.socialLinks.linkedin : `https://${guest.socialLinks.linkedin}`} target="_blank" rel="noopener" className="px-3 py-1 bg-secondary rounded-lg text-sm hover:bg-secondary/80">LinkedIn</a>}
                {guest.socialLinks?.youtube && <a href={guest.socialLinks.youtube.startsWith("http") ? guest.socialLinks.youtube : `https://${guest.socialLinks.youtube}`} target="_blank" rel="noopener" className="px-3 py-1 bg-secondary rounded-lg text-sm hover:bg-secondary/80">YouTube</a>}
                {!guest.socialLinks?.website && !guest.socialLinks?.twitter && !guest.socialLinks?.linkedin && !guest.socialLinks?.youtube && <span className="text-muted-foreground text-sm">No social links</span>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Episodes</CardTitle>
                <Button 
                  size="sm" 
                  onClick={handleScheduleEpisode}
                  disabled={scheduling}
                >
                  {scheduling ? (
                    <>
                      <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    "+ Schedule"
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {guest.episodes?.length === 0 ? <p className="text-muted-foreground text-sm">No episodes yet.</p> : (
                <div className="space-y-3">
                  {guest.episodes?.map(ep => (
                    <div key={ep.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div><div className="font-medium">{ep.title}</div></div>
                      <span className={`px-2 py-1 text-xs rounded-lg ${statusColors[ep.status]}`}>{ep.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <FollowUpsWidget guestId={guest.id} guestName={guest.name} />

          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {guest.tags?.length ? guest.tags.map(tag => <span key={tag} className="px-2 py-1 text-sm bg-secondary rounded-lg">{tag}</span>) : <span className="text-sm text-muted-foreground">No tags</span>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Episodes</span><span className="font-medium">{guest.totalEpisodes}</span></div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

interface FollowUp {
  id: string;
  notes: string;
  dueDate: number | null;
  completedAt: number | null;
}

function FollowUpsWidget({ guestId, guestName }: { guestId: string; guestName: string }) {
  const [followUps, setFollowUps] = useState<FollowUp[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState<string | null>(null);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    api.get(`/api/follow-ups?status=pending`)
      .then(r => r.json())
      .then(d => {
        const guestFollowUps = (d.followUps || []).filter((f: any) => f.guestId === guestId);
        setFollowUps(guestFollowUps);
        setLoading(false);
      });
  }, [guestId]);

  const createFollowUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    
    if (!notes.trim()) {
      setFormError("Please enter notes for the follow-up");
      return;
    }
    
    setSaving(true);
    try {
      const res = await api.post("/api/follow-ups", { guestId, notes, dueDate: dueDate || undefined });
      if (res.ok) {
        const data = await res.json();
        setFollowUps([data.followUp, ...followUps]);
        setShowForm(false);
        setNotes("");
        setDueDate("");
      } else {
        const error = await res.json();
        setFormError(error.error || "Failed to create follow-up");
      }
    } catch (err) {
      setFormError("An error occurred. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const completeFollowUp = async (id: string) => {
    setCompleting(id);
    try {
      const res = await api.post(`/api/follow-ups/${id}/complete`);
      if (res.ok) {
        setFollowUps(prev => prev.filter(f => f.id !== id));
      }
    } finally {
      setCompleting(null);
    }
  };

  const pending = followUps.filter(f => !f.completedAt);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Follow-ups</CardTitle>
          <Button size="sm" variant="ghost" onClick={() => setShowForm(!showForm)} className="flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showForm && (
          <form onSubmit={createFollowUp} className="mb-4 space-y-3 p-3 bg-muted/50 rounded-lg">
            {formError && (
              <div className="text-xs text-destructive bg-destructive/10 p-2 rounded">
                {formError}
              </div>
            )}
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder={`Follow up with ${guestName} about...`}
              className="w-full px-3 py-2 text-sm border rounded-lg bg-background"
              rows={2}
              disabled={saving}
            />
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded-lg bg-background"
              disabled={saving}
            />
            <div className="flex gap-2">
              <Button 
                type="submit" 
                size="sm" 
                className="flex-1"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save"
                )}
              </Button>
              <Button 
                type="button" 
                size="sm" 
                variant="outline" 
                onClick={() => { setShowForm(false); setFormError(""); }}
                disabled={saving}
              >
                Cancel
              </Button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : pending.length === 0 ? (
          <div className="text-sm text-muted-foreground">No pending follow-ups.</div>
        ) : (
          <div className="space-y-2">
            {pending.map(f => (
              <div key={f.id} className="flex items-start gap-2 p-2 bg-muted/30 rounded-lg">
                <button
                  onClick={() => completeFollowUp(f.id)}
                  disabled={completing === f.id}
                  className="mt-0.5 w-4 h-4 rounded border border-muted-foreground/30 hover:border-primary flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  title="Mark complete"
                >
                  {completing === f.id && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                </button>
                <div className="min-w-0">
                  <p className="text-sm truncate">{f.notes}</p>
                  {f.dueDate && (
                    <p className={`text-xs ${f.dueDate < Date.now() ? "text-destructive" : "text-muted-foreground"}`}>
                      Due: {new Date(f.dueDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
